import type { Schemas } from "@norn/sdk"
import { useQuery } from "@tanstack/react-query"
import { run, scopeQuery } from "@/lib/api"
import type { Scope } from "@/lib/connection"
import { parseExact } from "@/lib/lossless"
import { useCell } from "@/lib/session"
import type { LinkMeta, Ontology } from "@/workspace/ontology"

type Obj = Schemas["ObjectResponse"]

/** One move from a set of records to the records they link to (or that link to them). */
export interface PivotStep {
  readonly direction: "out" | "in"
  readonly link: LinkMeta
  /** The type of the records the step arrives at. */
  readonly to: string
}

export interface PivotOption extends PivotStep {
  readonly id: string
  readonly label: string
}

/** Every typed link a set of `typeName` records can be pivoted along, both directions. */
export function pivotOptions(ontology: Ontology, typeName: string): PivotOption[] {
  const type = ontology.types.get(typeName)
  if (!type) return []
  const out = type.links
    .filter((l) => ontology.types.has(l.target))
    .map((l) => ({ id: `out:${l.id}`, direction: "out" as const, link: l, to: l.target, label: `${l.label} → ${ontology.types.get(l.target)?.pluralLabel ?? l.target}` }))
  const inbound = (ontology.incoming.get(typeName) ?? [])
    .filter((l) => ontology.types.has(l.source))
    .map((l) => ({ id: `in:${l.source}.${l.id}`, direction: "in" as const, link: l, to: l.source, label: `${ontology.types.get(l.source)?.pluralLabel ?? l.source} · ${l.label.toLowerCase()}` }))
  return [...out, ...inbound]
}

/** The property carrying a link's target key on its source type, when the ontology declares one. */
export function keyField(ontology: Ontology, link: LinkMeta): string | undefined {
  return ontology.types.get(link.source)?.fields.find((f) => f.link === link.id)?.id
}

const SCAN_PAGE = 1000
const SCAN_PAGES = 2

/** Up to 2,000 authorized records of one type, read once for pivots (a truncated scan says so). */
export function useTypeScan(scope: Scope, objectType: string | undefined) {
  const { connection, client } = useCell()
  return useQuery({
    queryKey: ["dv-scan", connection.baseUrl, scope.orgId, scope.environmentId, objectType],
    enabled: Boolean(objectType),
    staleTime: 120_000,
    retry: false,
    queryFn: async ({ signal }) => {
      const items: Obj[] = []
      let cursor: string | null = null
      let pages = 0
      do {
        const page = parseExact(
          await run(connection.baseUrl, () =>
            client.GET("/v1/objects/{object_type}", {
              params: { path: { object_type: objectType as string }, query: { ...scopeQuery(scope), limit: SCAN_PAGE, ...(cursor ? { cursor } : {}) } },
              parseAs: "text",
              signal,
            }),
          ),
        ) as Schemas["ObjectList"]
        items.push(...page.items)
        cursor = page.nextCursor ?? null
        pages += 1
      } while (cursor && pages < SCAN_PAGES)
      return { items, truncated: cursor !== null }
    },
  })
}

/** A key a record carries in a property, unless the cell withheld it. */
function readableKey(o: Obj, property: string): string | null {
  const masked = o.masked as Record<string, unknown> | undefined
  const v = (o.values as Record<string, unknown>)[property]
  return typeof v === "string" && !(masked && property in masked) ? v : null
}

/**
 * The records one pivot step reaches from `from`, by the link's declared key property: outgoing
 * steps keep target records whose key the source records name; incoming steps keep source-type
 * records naming one of `from`. Withheld keys never join. Links without a key property return null
 * (the caller explains that this link can only be followed record by record, in the graph).
 */
export function pivotRows(ontology: Ontology, step: PivotStep, from: readonly Obj[], scanned: readonly Obj[]): Obj[] | null {
  const field = keyField(ontology, step.link)
  if (!field) return null
  if (step.direction === "out") {
    const keys = new Set(from.map((o) => readableKey(o, field)).filter((k): k is string => k !== null))
    return scanned.filter((o) => keys.has(o.key))
  }
  const keys = new Set(from.map((o) => o.key))
  return scanned.filter((o) => {
    const k = readableKey(o, field)
    return k !== null && keys.has(k)
  })
}
